import { useEffect, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { useOrg } from "@/contexts/OrgContext";
import * as actions from "@/redux/actions";
import SntOrgMenuSelectorContainer from "@containers/SntOrgSelectorContainer/SntOrgMenuSelectorContainer";

import NavList from "./NavList/NavList";
import useHomeMenu from "./Menus/useHomeMenu";
import useSelectedOrgMenu from "./Menus/useSelectedOrgMenu";
import usePartnerMenu from "./Menus/usePartnerMenu";
import useSystemMenu from "./Menus/useSystemMenu";

import HomeIcon from "@images/menu/home.svg";
import CogIcon from "@images/menu/cog.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import MenuType from "./Menus/menuTypeConst";
import { getHashFromLandingPage } from "@/containers/LandingPageHelper/LandingPageHelper";

const SidebarBody = () => {
  const { orgId, updateOrg } = useOrg();
  const language = useSelector((state) => state.language);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state) => state.user);
  const { permissionsMap, whiteLabel } = loginInfo;

  const homeMenu = useHomeMenu(loginInfo);
  const selectedOrgMenu = useSelectedOrgMenu(loginInfo, orgId);
  const partnerMenu = usePartnerMenu(loginInfo);
  const systemMenu = useSystemMenu(loginInfo);

  const [selectedMenu, setSelectedMenu] = useState(homeMenu);

  const [lastHomeMenu, setLastHomeMenu] = useState(null);
  const [lastAdminMenu, setLastAdminMenu] = useState(null);
  const [lastSelelectedOrgMenu, setLastSelectedOrgMenu] = useState(null);
  const [lastAllMenu, setLastAllMenu] = useState(null);

  const [showFooter, setShowFooter] = useState(false);

  const findRouteInAllPaths = (allPaths, path) => {
    // First try exact match
    if (allPaths[path]) {
      return allPaths[path];
    }
    // Then try prefix match for paths with dynamic segments (e.g., /site_map_view/514)
    for (const key in allPaths) {
      if (path.startsWith(key + "/")) {
        return allPaths[key];
      }
    }
    return null;
  };

  const getMenuByLocationPath = (path) => {
    let routeItem = findRouteInAllPaths(homeMenu.allPaths, path);
    if (routeItem) {
      return homeMenu;
    }

    routeItem = findRouteInAllPaths(selectedOrgMenu.allPaths, path);
    if (routeItem) {
      return selectedOrgMenu;
    }

    if (loginInfo?.organisation?.organisationType === "PARTNER") {
      routeItem = findRouteInAllPaths(partnerMenu.allPaths, path);
      if (routeItem) {
        return partnerMenu;
      }
    } else if (loginInfo?.organisation?.organisationType === "SYSTEM") {
      routeItem = findRouteInAllPaths(systemMenu.allPaths, path);
      if (routeItem) {
        return systemMenu;
      }
    }

    return homeMenu;
  };

  useEffect(() => {
    if (
      homeMenu.loading ||
      selectedOrgMenu.loading ||
      partnerMenu.loading ||
      systemMenu.loading
    ) {
      return;
    }

    let menu = getMenuByLocationPath(location.pathname);
    setSelectedMenu(menu);
    dispatch(actions.setMenuType(menu.type));

    // empty menu
    if (
      Object.keys(menu.availableMenuPaths).length === 0 &&
      location.pathname !== "/permission"
    ) {
      history.push("/permission");
      return;
    }
    // protected route
    const mainRoute = menu.allPaths[location.pathname];
    if (!mainRoute && location.pathname !== "/") return;
    if (
      location.pathname !== "/no_access" &&
      location.pathname !== "/permission" &&
      location.pathname !== "/"
    ) {
      if (
        (location.pathname.includes("/bi_dashboard/") &&
          !menu.availableMenuPaths["/all_dashboards"]) ||
        (!location.pathname.includes("/bi_dashboard/") &&
          (!mainRoute ||
            !(
              menu.availableMenuPaths[mainRoute.to] ||
              menu.availableMenuPaths[location.pathname]
            )))
      ) {
        if (menu.forceMenuPaths[mainRoute.to]) {
          let path = menu.defaultPath;
          history.push(`${path.pathname}?org=${orgId}`);
        } else {
          history.push("/no_access");
        }
        return;
      }
    }

    if (menu.type === MenuType.ADMIN) {
      let adminMatchRoute = selectedOrgMenu.allPaths[location.pathname];
      if (
        adminMatchRoute &&
        !adminMatchRoute.subPages?.find((p) => p === location.pathname) &&
        !orgId
      ) {
        // top level -> need org id for admin url, update to own org
        updateOrg(loginInfo.organisation.id);
      }
      setLastAdminMenu(location);
      setLastSelectedOrgMenu(location);
    } else if (menu.type === MenuType.MAIN) {
      setLastHomeMenu(location);
    } else {
      setLastAdminMenu(location);
      setLastAllMenu(location);
    }

    if (location.pathname === "" || location.pathname === "/") {
      let landingPath = {
        pathname: getHashFromLandingPage(loginInfo),
        search: "",
      };
      if (!homeMenu.availableMenuPaths[landingPath.pathname]) {
        landingPath = homeMenu.defaultPath;
      }
      history.push(landingPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedOrgMenu, partnerMenu, systemMenu, homeMenu]);

  useEffect(() => {
    if (selectedOrgMenu.isAdminMenuAvailable) {
      setShowFooter(true);
      return;
    }

    let isShowFooter = false;
    if (
      permissionsMap["SYSTEM_ADMIN_VIEW"] &&
      systemMenu.isAdminMenuAvailable
      // Object.keys(systemMenu.availableMenuPaths).length !== 0
    ) {
      isShowFooter = true;
    } else if (
      loginInfo?.organisation?.organisationType === "PARTNER" &&
      partnerMenu.isAdminMenuAvailable
      // Object.keys(partnerMenu.availableMenuPaths).length !== 0
    ) {
      isShowFooter = true;
    }
    setShowFooter(isShowFooter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgMenu, partnerMenu, systemMenu]);

  const onOrganisationChanged = (data) => {
    if (data.value) {
      if (orgId) {
        let selectedMatchRoute = selectedMenu.allPaths[location.pathname];
        // location is detail page, back to overview menu when orgId changed. If not chnage orgId
        if (
          selectedMatchRoute?.subPages?.find((p) => p === location.pathname)
        ) {
          history.push(`${selectedMatchRoute.to}?org=${data.value}`);
        } else {
          updateOrg(data.value);
        }
      } else {
        // ALL menu back to selected org Menu
        let path = lastSelelectedOrgMenu || selectedOrgMenu.defaultPath;
        history.push(`${path.pathname}?org=${data.value}`);
      }
    } else {
      let path = "";
      if (loginInfo?.organisation?.organisationType === "PARTNER") {
        path = lastAllMenu || partnerMenu.defaultPath;
      } else {
        path = lastAllMenu || systemMenu.defaultPath;
      }
      history.push(path);
    }
  };

  const onAdminButtonClicked = () => {
    if (lastAdminMenu) {
      history.push(lastAdminMenu);
    } else {
      let organisationType = loginInfo?.organisation?.organisationType;
      if (organisationType === "NORMAL") {
        history.push(selectedOrgMenu.defaultPath);
      } else if (organisationType === "PARTNER") {
        history.push(partnerMenu.defaultPath);
      } else {
        history.push(systemMenu.defaultPath);
      }
    }
  };

  const onHomeMenuClicked = () => {
    let path = lastHomeMenu || homeMenu.defaultPath;
    setSelectedMenu(homeMenu);
    history.push(path);
  };

  return (
    <>
      {selectedMenu.type !== homeMenu.type &&
        (loginInfo?.organisation?.organisationType === "SYSTEM" ||
          loginInfo?.organisation?.organisationType === "PARTNER") && (
          <div
            style={{
              zIndex: 2005,
              backgroundColor: "var(--admin-sidebar-bg-color)",
            }}
          >
            <div style={{ padding: 10, width: "100%" }}>
              <SntOrgMenuSelectorContainer
                onChange={onOrganisationChanged}
                value={orgId === null ? 0 : orgId}
                language={language}
              />
            </div>
          </div>
        )}
      <div className="sidebar-middle">
        <NavList
          orgId={orgId}
          location={location}
          routes={selectedMenu.routes}
          collapsible={selectedMenu.type === homeMenu.type}
          menuType={selectedMenu.type}
        />
      </div>
      {showFooter || !whiteLabel ? (
        <div
          className={
            "sidebar-footer " +
            (selectedMenu.type === MenuType.MAIN ? "home-menu" : "admin-menu")
          }
        >
          {showFooter && (
            <div className="nav-icon-bar">
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type === homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onHomeMenuClicked}
                >
                  <img alt="home" src={HomeIcon} />
                  <div>{language.home_key}</div>
                </div>
              </div>
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type !== homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onAdminButtonClicked}
                >
                  <img alt="admin" src={CogIcon} />
                  <div>{language.sidebar_admin_key}</div>
                </div>
              </div>
            </div>
          )}
          {!whiteLabel && (
            <div className="powered-by">
              <div>{language.powered_by_key}</div>
              <img src={SensolusLogoIcon} alt="sensolus" />
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default SidebarBody;
